import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, useResponsive } from '@common-fe/common-fe';

import { useSwipe } from '@/utils';

import { useGetPNLQuery } from './queries';
import SliderPLItem from './SliderPlItem';

export interface PLData {
  key: string;
  value: number;
  percents?: number;
  label: string;
  subLabel: string;
  hint: string;
  additionalHint: string;
}
interface Props {
  isUnwrappedMode?: boolean;
  parentRef?: React.RefObject<HTMLElement | null>;
}
const SliderPL: React.FC<Props> = ({ parentRef, isUnwrappedMode }) => {
  const { data, isLoading } = useGetPNLQuery();
  const { isMobile } = useResponsive();

  const list = useMemo<PLData[]>(
    () => [
      {
        key: '1',
        value: data?.month.amount || 0,
        percents: data?.month.percentage,
        label: '30 Day',
        subLabel: ' Gain/Loss',
        hint: 'The change over the past 30 days in the unrealized gains or losses that would be realized if the investment was sold at the current market price. The calculation uses the current market value of the investments minus its value at the start of the time period (30 days) and does not include any deposits or withdrawals. The calculation is made when funds settle for each trading day.',
        additionalHint: 'Past performance is not a guarantee or prediction of future investment results.',
      },
      {
        key: '2',
        value: data?.year.amount || 0,
        percents: data?.year.percentage,
        label: 'YTD',
        subLabel: 'Gain/Loss',
        hint: 'The change over the calendar year in the unrealized gains or losses that would be realized if the investment was sold at the current market price. The calculation uses the current market value of the investments minus its value at the start of the time period (first day of the year) and does not include any deposits or withdrawals. The calculation is made when funds settle for each trading day.',
        additionalHint: 'Past performance is not a guarantee or prediction of future investment results.',
      },
      {
        key: '3',
        value: data?.lifetime.amount || 0,
        percents: data?.lifetime.percentage,
        label: 'Lifetime',
        subLabel: 'Gain/Loss',
        hint: "The change over the lifetime of the portfolio in unrealized gains or losses that would be realized if the investment were sold at the current market price. This is calculated by subtracting the portfolio's value at the start of the time period (portfolio opening) from its current market value, excluding any deposits or withdrawals. The calculation is performed daily once funds settle.",
        additionalHint: 'Past performance does not guarantee or predict future investment results.',
      },
    ],
    [
      data?.lifetime.amount,
      data?.lifetime.percentage,
      data?.month.amount,
      data?.month.percentage,
      data?.year.amount,
      data?.year.percentage,
    ]
  );
  const [currentSlide, setCurrentSlide] = useState<PLData | null>(list[0] || null);

  useEffect(() => {
    setCurrentSlide(list[0] || null);
  }, [list]);

  const handleNextSlide = useCallback(() => {
    const foundIndex = list.findIndex((item) => item === currentSlide);
    if (foundIndex !== -1) {
      const nextItem = list[foundIndex + 1];
      if (nextItem) {
        setCurrentSlide(nextItem);
      } else {
        setCurrentSlide(list[0] || null);
      }
    }
  }, [currentSlide, list]);
  const handlePreviousSlide = useCallback(() => {
    const foundIndex = list.findIndex((item) => item === currentSlide);
    if (foundIndex !== -1) {
      const nextItem = list[foundIndex - 1];
      if (nextItem) {
        setCurrentSlide(nextItem);
      } else {
        setCurrentSlide(list[list.length - 1] || null);
      }
    }
  }, [currentSlide, list]);
  useSwipe(parentRef, {
    onSwipeLeft: () => {
      if (isMobile) {
        handlePreviousSlide();
      }
    },
    onSwipeRight: () => {
      if (isMobile) {
        handleNextSlide();
      }
    },
  });
  if (isUnwrappedMode) {
    return (
      <Box gap="spacing8" direction={isMobile ? 'row' : 'column'}>
        <SliderPLItem isSmallMode isLoading={isLoading} {...list[0]} />

        <SliderPLItem isSmallMode isLoading={isLoading} {...list[1]} />

        <SliderPLItem isSmallMode isLoading={isLoading} {...list[2]} />
      </Box>
    );
  }

  return (
    <SliderPLItem
      isLoading={isLoading}
      onPrevious={handlePreviousSlide}
      onNext={handleNextSlide}
      {...currentSlide}
    />
  );
};

export default SliderPL;
